import {
  type Auth,
  connectAuthEmulator,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type Unsubscribe,
  type User,
  type UserCredential,
} from 'firebase/auth';
import { firebaseClient } from './client';

export type { User, UserCredential };

let emulatorConnected = false;

function getAuthInstance(): Auth {
  const { auth } = firebaseClient;

  if (!emulatorConnected && process.env.NODE_ENV !== 'production') {
    emulatorConnected = true;
    connectAuthEmulator(auth, 'http://localhost:9099');
  }

  return auth;
}

export function getCurrentUser(): User | null {
  return getAuthInstance().currentUser;
}

export function observeAuthState(
  listener: (user: User | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return onError
    ? onAuthStateChanged(getAuthInstance(), listener, onError)
    : onAuthStateChanged(getAuthInstance(), listener);
}

export async function getCurrentUserIdToken(
  forceRefresh = true,
): Promise<string | null> {
  const user = getCurrentUser();

  if (!user) return null;

  return user.getIdToken(forceRefresh);
}

export function signInWithGoogle(): Promise<UserCredential> {
  return signInWithPopup(getAuthInstance(), new GoogleAuthProvider());
}

export function signInWithEmail(
  email: string,
  password: string,
): Promise<UserCredential> {
  return signInWithEmailAndPassword(getAuthInstance(), email, password);
}

export function createUserWithEmail(
  email: string,
  password: string,
): Promise<UserCredential> {
  return createUserWithEmailAndPassword(getAuthInstance(), email, password);
}

export function sendPasswordReset(email: string): Promise<void> {
  return sendPasswordResetEmail(getAuthInstance(), email);
}

export function signOutCurrentUser(): Promise<void> {
  return signOut(getAuthInstance());
}
